import { useState } from "react";
import { Link } from "react-router-dom";
import { get } from "./api.js";
import type { FamilySetupData } from "./familySetupApi.js";
import { useAsync } from "./useAsync.js";
import "./FamilyPurchaseNotice.css";

/** A family-wide action, so a different selected child cannot hide a purchase. */
export function FamilyPurchaseNotice({ accountId }: { accountId: string }) {
  const setup = useAsync(() => get<FamilySetupData>("/family-setup"), [accountId]);
  const [rechecked, setRechecked] = useState(false);
  const recheck = () => { setRechecked(true); setup.reload(); };
  const claims = setup.data?.claims ?? [];
  const ready = claims.filter((claim) => claim.state === "choose_learner" && Boolean(claim.purchase) && !setup.data?.familyCreditsEnabled);
  const waiting = claims.filter((claim) => claim.state === "choose_learner" && !claim.purchase).length;
  const accountReview = claims.filter((claim) => claim.state === "choose_learner" && Boolean(claim.purchase) && setup.data?.familyCreditsEnabled).length;
  const needsProof = claims.filter((claim) => claim.state === "needs_email_proof").length;
  const needsReview = claims.filter((claim) => claim.state === "needs_review").length;
  if (setup.data && !setup.data.enabled) return null;

  if (setup.error) return <section className="family-purchase-notice family-purchase-notice-compact" role="alert">
    <div><p className="family-purchase-notice-label">Purchase setup</p><p>Purchase status could not be loaded.</p></div>
    <button type="button" className="quiet small" onClick={setup.reload}>Try again</button>
  </section>;

  if (setup.loading && !setup.data) return null;

  if (ready.length > 0) {
    const count = ready.length;
    return <section className="family-purchase-notice" aria-labelledby="family-purchase-heading">
      <div>
        <p className="family-purchase-notice-label">Action needed</p>
        <h2 id="family-purchase-heading">{count === 1 ? "1 purchase is ready to assign" : `${count} purchases are ready to assign`}</h2>
        <p>Choose the learner for {count === 1 ? "this purchase" : "these purchases"}.</p>
        {setup.data && setup.data.pendingActionCount > count && <p className="small muted">{setup.data.pendingActionCount} purchases need setup in total. View setup for all next steps.</p>}
      </div>
      <Link className="button" to="/setup">{count === 1 ? "Assign your purchase" : "Review purchases"}</Link>
    </section>;
  }

  const statusCount = waiting + needsProof + needsReview + accountReview;
  if (statusCount === 0) return null;

  return <section className="family-purchase-notice family-purchase-notice-compact" aria-labelledby="family-purchase-status-heading">
    <div>
      <p className="family-purchase-notice-label">Purchase setup</p>
      <h2 id="family-purchase-status-heading">Next steps for your purchase</h2>
      <p>{waiting > 0 && "Purchase details are still arriving. "}{needsProof > 0 && "Email confirmation is needed. "}{(needsReview > 0 || accountReview > 0) && "The school is reviewing a purchase."}</p>
    </div>
    <div className="stack">
      {(waiting > 0 || accountReview > 0) && <button type="button" className="quiet small" disabled={setup.loading} onClick={recheck}>{setup.loading ? "Checking…" : "Check again"}</button>}
      <Link to="/setup">View setup</Link>
      {rechecked && !setup.loading && !setup.error && <p className="small muted" role="status">Status checked. {waiting > 0 ? "Purchase setup is still pending." : "The latest next steps are shown above."}</p>}
    </div>
  </section>;
}
